import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Box, Button, ButtonBase, CircularProgress, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography, useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import useLogout from '../../auth/useLogout';
import { storage } from '../../../services/storageService';
import { StorageKeys } from '../../../services/constants/StorageKeys';
import { useTenant } from '../../../contexts/TenantContext';
import { Routes } from '../../../routes/RouteConstants';
import { TOPBAR_HEIGHT } from './adminSections';
import { tint } from '../../../utils/textStyles';
import { useStaff } from '../StaffContext';
import useGetSignedInUser from '../api/useGetSignedInUser';
import PersonAvatar from '../components/PersonAvatar';

const DEFAULT_ON_BAR = '#fff';

/**
 * Who is signed in, as the bar's last item: the avatar, the name, and under it what they are here
 * („Admin”, „Borbély”, or both); on a phone only the avatar. A click opens the profile menu, which
 * holds „Kijelentkezés”.
 *
 * Signing out is a click, never a page load (docs/SECURITY_RISKS.md, rule 2). The item spins until
 * the server has answered; the local flag goes whatever the answer, as in the guest header. The
 * guest header is not on the page here, so landing on "/" mounts it afresh, already signed out.
 */
const ProfileMenu = ({ onBar }) => {
  const { displayConfig, hasSeveralProviders } = useTenant();
  const { loading, error, isAdmin, ownProviderId } = useStaff();
  const { data: user } = useGetSignedInUser({ enabled: !loading && !error });
  const { logout } = useLogout();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Being the provider says something only where there are several.
  const isOwnProvider = hasSeveralProviders && !!ownProviderId;
  const role = [isAdmin && 'Admin', isOwnProvider && displayConfig?.providerLabel].filter(Boolean).join(' · ');

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    await logout();
    storage.set(StorageKeys.IS_USER_AUTHENTICATED, false);
    navigate('/');
  };

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        data-testid="admin-profile"
        aria-label={user?.name ? `Profil: ${user.name}` : 'Profil'}
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
          p: 0.5,
          borderRadius: 999,
          color: onBar,
          '&:hover, &.Mui-focusVisible': { bgcolor: tint(onBar, 0.08) },
        }}
      >
        <PersonAvatar size={32} sx={{ flexShrink: 0 }} />
        {user?.name && (
          <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0, maxWidth: 220, textAlign: 'left' }}>
            <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.3 }}>
              {user.name}
            </Typography>
            {role && (
              <Typography noWrap sx={{ fontSize: '0.75rem', lineHeight: 1.3, color: tint(onBar, 0.7) }}>
                {role}
              </Typography>
            )}
          </Box>
        )}
        <ExpandMoreIcon fontSize="small" sx={{ flexShrink: 0, opacity: 0.8 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2 } }}
      >
        {/* The full name, which the bar may have cut short or, on a phone, not shown at all. */}
        {user?.name && (
          <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
            <Typography fontWeight={600} fontSize={14} sx={{ overflowWrap: 'anywhere' }}>{user.name}</Typography>
            {user.email && (
              <Typography fontSize={12.5} color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{user.email}</Typography>
            )}
          </Box>
        )}
        {user?.name && <Divider />}
        <MenuItem
          onClick={handleLogout}
          disabled={isLoggingOut}
          aria-busy={isLoggingOut}
          data-testid="admin-logout"
          sx={{ fontSize: 14, '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.07)', color: 'error.main' } }}
        >
          <ListItemIcon sx={{ color: 'inherit' }}>
            {isLoggingOut ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon fontSize="small" />}
          </ListItemIcon>
          Kijelentkezés
        </MenuItem>
      </Menu>
    </>
  );
};

/**
 * The admin area's bar. It keeps the tenant's header colour and logo, so the admin still knows
 * whose site this is, but none of the guest menu: that is one click away, behind „Vendég oldal”.
 * On a phone that button is at the foot of the menu drawer instead (AdminSidebar).
 */
const AdminTopBar = ({ showMenuButton, showGuestSiteButton, onMenuClick }) => {
  const theme = useTheme();
  const { tenantId, displayConfig } = useTenant();
  // The tenant's header ink: white on a dark bar, black on a white one.
  const onBar = displayConfig?.headerColor || DEFAULT_ON_BAR;

  return (
    <Box
      component="header"
      sx={{
        height: TOPBAR_HEIGHT,
        flexShrink: 0,
        bgcolor: displayConfig?.headerBackground || '#000',
        color: onBar,
        display: 'flex',
        alignItems: 'center',
        px: { xs: 1, sm: 2.5 },
        gap: { xs: 1, sm: 2 },
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
      }}
    >
      {showMenuButton && (
        <IconButton onClick={onMenuClick} aria-label="Menü megnyitása" sx={{ color: onBar }}>
          <MenuIcon />
        </IconButton>
      )}

      <Box component={NavLink} to={Routes.DASHBOARD} sx={{ display: 'flex', height: TOPBAR_HEIGHT }}>
        <img src={`/${tenantId}/logo.png`} alt="Logó" style={{ height: TOPBAR_HEIGHT, display: 'block' }} />
      </Box>

      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 2 }}>
        <Box component="span" sx={{ opacity: 0.4 }}>|</Box>
        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 500, opacity: 0.85 }}>
          Admin felület
        </Typography>
      </Box>

      <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
        {showGuestSiteButton && (
          <Button
            component={NavLink}
            to="/"
            data-testid="admin-guest-site"
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{
              color: onBar,
              borderColor: tint(onBar, 0.5),
              textTransform: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': { borderColor: onBar, bgcolor: tint(onBar, 0.08) },
            }}
          >
            Vendég oldal
          </Button>
        )}
        <ProfileMenu onBar={onBar} />
      </Box>
    </Box>
  );
};

export default AdminTopBar;
